import React, { useState, useEffect } from "react";
import { Menu, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";
import Icons from "components/icons";
import { Localize } from "components/service";
import { usePermissionsContext } from "components/permissions";
import VariableConst from "components/variable/variable-const";
import GraphicConst from "components/graphic/graphic-const";
import UserConst from "components/user/users-const";
import RoleConst from "components/role/role-const";
import OrganizerConst from "components/organizer/organizer-const";
import ExhibitionConst from "components/exhibition/exhibition-const";
import SupplierConst from "components/supplier/supplier-const";
import TeamConst from "components/team/team-const";
import StandConst from "components/stand/stand-const";
import EquipmentConst from "components/equipment/equipment-const";
import FlooringConst from "components/flooring/flooring-const";
import "./sidebar.scss";


interface IMenuItem
{
    key: string;
    label: JSX.Element;
    onClick: () => void;
}

interface ISidebarProps
{
    appParams: {
        activeMenu: {
            selectedKeys?: string[];
            openKeys?: string[];
        };
    };
    setToggleSider: (value: boolean) => void;
}

const Sidebar: React.FC<ISidebarProps> = ({ appParams, setToggleSider }): JSX.Element =>
{
    const { selectedKeys = [ "dashboard" ], openKeys = [ "dashboard" ] } =
        appParams.activeMenu;

    const [ openSubMenu, setOpenSubMenu ] = useState<string[]>(openKeys);
    const { hasPermission } = usePermissionsContext();
    const navigate = useNavigate();

    useEffect(() =>
    {
        setOpenSubMenu(openKeys);
    }, [ appParams.activeMenu, openKeys ]);

    const handleSmartNavigation = (routes: Array<{ permission: any; path: string }>) =>
    {
        setToggleSider(true);

        // Check rights in order of priority
        for (const route of routes) {
            if (hasPermission(route.permission.slug)) {
                navigate(route.path);
                return;
            }
        }

        // If there are no rights to any root, we show 403
        navigate("/403");
    };

    const handleSettingsClick = () => handleSmartNavigation([
        { permission: VariableConst.permissions.VIEW, path: "/variable-lists" },
        { permission: GraphicConst.permissions.VIEW, path: "/graphic-rules" }
    ]);

    const handleUsersClick = () => handleSmartNavigation([
        { permission: UserConst.permissions.VIEW, path: "/users" },
        { permission: RoleConst.permissions.VIEW, path: "/user-roles" }
    ]);

    const handleOrganizersClick = () => handleSmartNavigation([
        { permission: OrganizerConst.permissions.VIEW, path: "/organizers" },
        { permission: ExhibitionConst.permissions.VIEW, path: "/exhibition-types" }
    ]);

    // Check if user has access to any sub-items for smart navigation
    const hasUsersAccess = hasPermission(UserConst.permissions.VIEW.slug) || hasPermission(RoleConst.permissions.VIEW.slug);
    const hasOrganizersAccess = hasPermission(OrganizerConst.permissions.VIEW.slug) || hasPermission(ExhibitionConst.permissions.VIEW.slug);
    const hasSettingsAccess = hasPermission(VariableConst.permissions.VIEW.slug) || hasPermission(GraphicConst.permissions.VIEW.slug);

    // Check individual permissions for regular menu items
    const hasSuppliersAccess = hasPermission(SupplierConst.permissions.VIEW.slug);
    const hasTeamsAccess = hasPermission(TeamConst.permissions.VIEW.slug);
    const hasStandsAccess = hasPermission(StandConst.permissions.VIEW.slug);
    const hasEquipmentAccess = hasPermission(EquipmentConst.permissions.VIEW.slug);
    const hasFlooringAccess = hasPermission(FlooringConst.permissions.VIEW.slug);

    // Menu item keys mapping using basePath from constants
    const MENU_KEYS = {
        USERS: UserConst.basePath.replace("/", ""),
        ORGANIZERS: OrganizerConst.basePath.replace("/", ""),
        SUPPLIERS: SupplierConst.basePath.replace("/", ""),
        TEAMS: TeamConst.basePath.replace("/", ""),
        STANDS: StandConst.basePath.replace("/", ""),
        EQUIPMENT: EquipmentConst.basePath.replace("/", ""),
        FLOORING: FlooringConst.basePath.replace("/", ""),
        VARIABLE_LISTS: VariableConst.basePath.replace("/", ""),
    };

    const items: IMenuItem[] = [
        {
            key: MENU_KEYS.USERS,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Users</Localize>}
                >
                    <a onClick={handleUsersClick}>
                        <Icons.Menu.Users />
                    </a>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.ORGANIZERS,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Organizers</Localize>}
                >
                    <a onClick={handleOrganizersClick}>
                        <Icons.Menu.Organizers />
                    </a>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.SUPPLIERS,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Suppliers</Localize>}
                >
                    <Link to="/suppliers">
                        <Icons.Menu.Suppliers />
                    </Link>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.TEAMS,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Teams</Localize>}
                >
                    <Link to="/teams">
                        <Icons.Menu.Teams />
                    </Link>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.STANDS,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_StandTypes</Localize>}
                >
                    <Link to="/stand-types">
                        <Icons.Menu.Stands />
                    </Link>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.EQUIPMENT,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Equipment</Localize>}
                >
                    <Link to="/equipment">
                        <Icons.Menu.Equipment />
                    </Link>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.FLOORING,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Flooring</Localize>}
                >
                    <Link to="/flooring">
                        <Icons.Menu.Flooring />
                    </Link>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
        {
            key: MENU_KEYS.VARIABLE_LISTS,
            label: (
                <Tooltip
                    overlayClassName="menu-tooltip"
                    placement="right"
                    title={<Localize>MENUS_DROP.Label_Settings</Localize>}
                >
                    <a onClick={handleSettingsClick}>
                        <Icons.Menu.Settings />
                    </a>
                </Tooltip>
            ),
            onClick: () => setToggleSider(true),
        },
    ].filter(item => {
        // Filter out menu items based on permissions
        switch (item.key) {
            case MENU_KEYS.USERS:
                return hasUsersAccess;
            case MENU_KEYS.ORGANIZERS:
                return hasOrganizersAccess;
            case MENU_KEYS.SUPPLIERS:
                return hasSuppliersAccess;
            case MENU_KEYS.TEAMS:
                return hasTeamsAccess;
            case MENU_KEYS.STANDS:
                return hasStandsAccess;
            case MENU_KEYS.EQUIPMENT:
                return hasEquipmentAccess;
            case MENU_KEYS.FLOORING:
                return hasFlooringAccess;
            case MENU_KEYS.VARIABLE_LISTS:
                return hasSettingsAccess;
            default:
                return true; // Show other items by default
        }
    });


    return (
        <>
            <Menu
                className="sidebar-menu"
                mode="inline"
                selectedKeys={selectedKeys}
                openKeys={openSubMenu}
                onOpenChange={(keys) => setOpenSubMenu(keys)}
                style={{ borderRight: 0 }}
                items={items.map((item) => ({
                    key: item.key,
                    label: item.label,
                    onClick: item.onClick,
                }))}
            />
        </>
    );
};

export default Sidebar;